import { Topbar } from "../components/Topbar";
import { KpiCard } from "../components/KpiCard";
import { Panel } from "../components/Panel";
import { LoadingState, ErrorState } from "../components/States";
import { useDashboard } from "../hooks/useDashboard";
import { useFilters } from "../context/FiltersContext";
import { formatCurrency, formatHours, formatNumber, formatPct } from "../lib/dates";
import { Building2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

export default function Empresas() {
  const { data, loading, error } = useDashboard("companies_overview");
  const { setSelectedCompanyIds } = useFilters();
  const navigate = useNavigate();

  const drillTo = (companyId, route) => {
    // Set the global company filter to just this company, then navigate
    setSelectedCompanyIds([companyId]);
    navigate(route);
  };

  return (
    <>
      <Topbar title="Resumen empresas" subtitle="KPIs comparados por empresa del grupo" />
      <div className="p-6 space-y-6" data-testid="empresas-page">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}
        {data && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {data.companies.map((c) => (
                <div
                  key={c.id}
                  className="bg-white border border-slate-200 rounded-md p-5 hover:border-slate-300 transition-colors duration-150 flex flex-col"
                  data-testid={`company-card-${c.id}`}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded bg-[#002FA7]/10 flex items-center justify-center shrink-0">
                      <Building2 className="w-5 h-5 text-[#002FA7]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                      <div className="text-xs text-slate-500">
                        {c.active_projects} proyectos activos · {formatHours(c.hours)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Ingresos</div>
                      <div className="text-lg font-medium tabular-nums text-slate-900">{formatCurrency(c.revenue)}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Gastos</div>
                      <div className="text-lg font-medium tabular-nums text-slate-900">{formatCurrency(c.expense)}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Margen</div>
                      <div className={`text-lg font-medium tabular-nums ${c.margin >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {formatCurrency(c.margin)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Margen %</div>
                      <div className={`text-lg font-medium tabular-nums ${c.margin_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {formatPct(c.margin_pct)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-slate-500">Ventas</span>
                      <div className="text-slate-900 font-medium tabular-nums">{formatCurrency(c.sales_amount)}</div>
                      <div className="text-slate-400">{formatNumber(c.sales_count, { maximumFractionDigits: 0 })} pedidos</div>
                    </div>
                    <div>
                      <span className="text-slate-500">Compras</span>
                      <div className="text-slate-900 font-medium tabular-nums">{formatCurrency(c.purchase_amount)}</div>
                      <div className="text-slate-400">{formatNumber(c.purchase_count, { maximumFractionDigits: 0 })} órdenes</div>
                    </div>
                  </div>

                  {/* Drilldown actions */}
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs">
                    <button
                      onClick={() => drillTo(c.id, "/app/pnl-analitico")}
                      data-testid={`drill-pnl-${c.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-slate-200 text-slate-700 hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors duration-150"
                    >
                      Ver P&G analítico <ArrowRight className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => drillTo(c.id, "/app/rentabilidad")}
                      data-testid={`drill-rent-${c.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-slate-200 text-slate-700 hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors duration-150"
                    >
                      Rentabilidad <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {data.companies.length > 1 && (
              <Panel title="Comparativa entre empresas" subtitle="Ingresos vs gastos" testId="chart-companies-comparison">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.companies}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => formatCurrency(v)} />
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="revenue" name="Ingresos" fill="#002FA7" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="expense" name="Gastos" fill="#94A3B8" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            )}

            {!data.companies.length && (
              <div className="text-center py-12 text-sm text-slate-400">
                Selecciona al menos una empresa en el filtro superior
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-xs text-amber-800">
              <strong>Multi-empresa:</strong> usa el selector <em>Empresas</em> en la barra superior para
              filtrar el grupo o comparar empresas. Los datos se cruzan con tu nivel de acceso en Odoo.
            </div>
          </>
        )}
      </div>
    </>
  );
}
